import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as authService from '../../services/authService';
import CampoSenha from '../../components/form/CampoSenha';

const EMPRESAS_SEED_DEV = [
  { empresaId: '990', nome: 'EMPRESA TESTE' },
  { empresaId: '001', nome: 'EMPRESA DEMO' },
];

const FILIAIS_SEED_DEV = [{ filialId: '01', nome: 'FILIAL 01' }];

/**
 * Extrai lista de empresas/filiais de formatos variados da API.
 */
function normalizarLista(resposta, idKey) {
  const bruto = resposta?.data ?? resposta;
  const lista = Array.isArray(bruto) ? bruto : bruto?.rows ?? bruto?.data ?? [];
  if (!Array.isArray(lista) || lista.length === 0) return [];
  return lista.map((item) => ({
    ...item,
    empresaId: item.empresaId ?? item.id,
    filialId: item.filialId ?? item.id,
    nome: item.nome ?? item.razaoSocial ?? item.descricao ?? String(item[idKey] ?? item.id ?? ''),
  }));
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { estaAutenticado, login, carregandoSessao } = useAuth();

  const [loginUsuario, setLoginUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [filialId, setFilialId] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [carregandoListas, setCarregandoListas] = useState(true);
  const [enviando, setEnviando] = useState(false);
  // O0c: motivo da saída forçada (ex.: perfil bloqueado pelo administrador)
  const [erro, setErro] = useState(() => location.state?.aviso || '');

  useEffect(() => {
    let ativo = true;
    const carregarEmpresas = async () => {
      setCarregandoListas(true);
      try {
        const resposta = await authService.listarEmpresas();
        const lista = normalizarLista(resposta, 'empresaId');
        if (ativo && lista.length > 0) {
          setEmpresas(lista);
          setEmpresaId((atual) =>
            lista.some((e) => String(e.empresaId) === String(atual))
              ? atual
              : String(lista[0].empresaId)
          );
        } else if (ativo) {
          // Fallback só se a API não retornar nada (offline)
          setEmpresas(EMPRESAS_SEED_DEV);
          setEmpresaId('990');
        }
      } catch {
        if (ativo) {
          setEmpresas(EMPRESAS_SEED_DEV);
          setEmpresaId('990');
        }
      } finally {
        if (ativo) setCarregandoListas(false);
      }
    };
    carregarEmpresas();
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (!empresaId) return undefined;
    let ativo = true;
    const carregarFiliais = async () => {
      try {
        const resposta = await authService.listarFiliais(empresaId);
        const lista = normalizarLista(resposta, 'filialId');
        if (ativo && lista.length > 0) {
          setFiliais(lista);
          setFilialId((atual) =>
            lista.some((f) => String(f.filialId) === String(atual))
              ? atual
              : String(lista[0].filialId)
          );
        } else if (ativo) {
          setFiliais(FILIAIS_SEED_DEV);
          setFilialId('01');
        }
      } catch {
        if (ativo) {
          setFiliais(FILIAIS_SEED_DEV);
          setFilialId('01');
        }
      }
    };
    carregarFiliais();
    return () => {
      ativo = false;
    };
  }, [empresaId]);

  if (carregandoSessao) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress aria-label="Carregando" />
      </Box>
    );
  }

  if (estaAutenticado) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (evento) => {
    evento.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      await login({
        login: loginUsuario.trim(),
        senha,
        empresaId,
        filialId,
      });
      navigate('/', { replace: true });
    } catch (err) {
      const mensagem =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Falha no login. Verifique credenciais e contexto.';
      setErro(mensagem);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', display: 'flex', bgcolor: 'background.default' }}>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, bgcolor: 'primary.main', alignItems: 'center', justifyContent: 'center', p: 6 }}>
        <Box component="img" src="/design-system/logos/terablues-logo-principal-fundo-azul.png" alt="TeraBlues" sx={{ width: 280, maxWidth: '60%' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper variant="outlined" elevation={0} sx={{ p: 4, width: '100%', maxWidth: 420, borderRadius: '12px' }} component="form" onSubmit={handleSubmit}>
        <Box component="img" src="/design-system/logos/terablues-logo-horizontal-fundo-branco.png" alt="TeraBlues" sx={{ display: { xs: 'block', md: 'none' }, mx: 'auto', height: 56, mb: 3 }} />
        {/* <Typography variant="h2" component="h1" align="center" gutterBottom>
          TeraBlues — Login
        </Typography> */}
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Informe usuário, senha, empresa e filial.
        </Typography>

        {erro ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        ) : null}

        <TextField
          label="Login"
          name="login"
          value={loginUsuario}
          onChange={(e) => setLoginUsuario(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          autoComplete="username"
          autoFocus
          required
        />
        <CampoSenha
          label="Senha"
          name="senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          autoComplete="current-password"
          required
        />

        <FormControl fullWidth margin="normal" size="small" disabled={carregandoListas}>
          <InputLabel id="empresa-login-label" shrink>Empresa</InputLabel>
          <Select
            labelId="empresa-login-label"
            label="Empresa"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
          >
            {empresas.map((empresa) => (
              <MenuItem key={empresa.empresaId} value={String(empresa.empresaId)}>
                {empresa.nome} ({empresa.empresaId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="filial-login-label" shrink>Filial</InputLabel>
          <Select
            labelId="filial-login-label"
            label="Filial"
            value={filialId}
            onChange={(e) => setFilialId(e.target.value)}
          >
            {filiais.map((filial) => (
              <MenuItem key={filial.filialId} value={String(filial.filialId)}>
                {filial.nome} ({filial.filialId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 3, height: 40, borderRadius: '4px' }}
          disabled={enviando || !loginUsuario || !senha || !empresaId || !filialId}
        >
          {enviando ? <CircularProgress aria-label="Carregando" size={24} color="inherit" /> : 'Entrar'}
        </Button>
      </Paper>
      </Box>
    </Box>
  );
};

export default Login;
